import {
  SEM_COLOR_ACTION_TERTIARY_FOREGROUND_ICON_DEFAULT_DARK,
  SEM_COLOR_ACTION_TERTIARY_FOREGROUND_ICON_DEFAULT_LIGHT,
  SEM_COLOR_FEEDBACK_SURFACE_INFORMATIVE_DARK,
  SEM_TYPOGRAPHY_NAVIGATION_SM_LABEL_NORMAL,
  SYS_BORDER_RADIUS_NONE,
  SYS_BORDER_RADIUS_MAX,
  SYS_BORDER_RADIUS_MD,
  SYS_BORDER_RADIUS_SM,
  SYS_COLOR_CANVAS_100_DARK,
  SYS_COLOR_CANVAS_100_LIGHT,
  SYS_COLOR_ELEVATION_LEVEL2_FILL_STOPS_0_DARK,
  SYS_COLOR_ELEVATION_LEVEL2_FILL_STOPS_0_LIGHT,
  SYS_COLOR_ELEVATION_LEVEL2_FILL_STOPS_100_DARK,
  SYS_COLOR_ELEVATION_LEVEL2_FILL_STOPS_100_LIGHT,
  SYS_FONT_SIZES_RELATIVE_85,
  SEM_COLOR_UI_FOREGROUND_DIVIDER_DEFAULT_DARK,
  SEM_COLOR_UI_FOREGROUND_DIVIDER_DEFAULT_LIGHT,
  SYS_COLOR_FOREGROUND_INTERACTIVE_NAVIGATION_200_DARK,
  SYS_COLOR_FOREGROUND_INTERACTIVE_NAVIGATION_200_LIGHT,
} from '@oneaudi/unified-web-alpha-common';
import type { CustomTheme } from '../@types/theme';
import {
  GLOBAL_COLOR_CANVAS_DEFAULT_DARK,
  GLOBAL_COLOR_CANVAS_DEFAULT_LIGHT,
  GLOBAL_COLOR_ELEVATION_LEVEL3_DARK,
  GLOBAL_COLOR_ELEVATION_LEVEL3_LIGHT,
  SEM_COLOR_ACTION_PRIMARY_FOREGROUND_TEXT_DEFAULT_DARK,
  SEM_COLOR_NAVIGATION_FOREGROUND_TEXT_ACTIVE_DARK,
  SEM_COLOR_NAVIGATION_FOREGROUND_TEXT_ACTIVE_LIGHT,
  SEM_COLOR_NAVIGATION_FOREGROUND_TEXT_IDLE_DARK,
  SEM_COLOR_NAVIGATION_FOREGROUND_TEXT_IDLE_LIGHT,
  SYS_BORDER_RADIUS_XS,
  SYS_BREAKPOINT_2_XL,
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_SM,
  SYS_BREAKPOINT_XL,
  SYS_BREAKPOINT_XS,
  SYS_COLOR_FOREGROUND_NEUTRAL_200_DARK,
  SYS_COLOR_FOREGROUND_NEUTRAL_200_LIGHT,
  SYS_COLOR_FOREGROUND_NEUTRAL_400_DARK,
  SYS_COLOR_SURFACE_HIGHLIGHT_PRIMARY_DARK,
  SYS_COLOR_SURFACE_HIGHLIGHT_PRIMARY_LIGHT,
  SYS_COLOR_SURFACE_INTERACTIVE_SECONDARY_100_DARK,
  SYS_COLOR_SURFACE_INTERACTIVE_SECONDARY_100_LIGHT,
  SYS_COLOR_SURFACE_INTERACTIVE_SECONDARY_300_DARK,
  SYS_COLOR_SURFACE_INTERACTIVE_SECONDARY_300_LIGHT,
  SYS_FONT_SIZES_RELATIVE_100,
  SYS_FONT_SIZES_RELATIVE_112_5,
  SYS_FONT_SIZES_RELATIVE_125,
  SYS_FONT_SIZES_RELATIVE_75,
  SYS_MAX_CONTENT_WIDTH,
  SYS_SIZE_FIXED_2400,
  SYS_SIZE_FIXED_600,
  SYS_SIZE_FIXED_800,
  SYS_SPACE_RELATIVE_2_XL,
  SYS_SPACE_RELATIVE_4_XL,
  SYS_SPACE_RELATIVE_LG,
  SYS_SPACE_RELATIVE_MD,
  SYS_SPACE_RELATIVE_SM,
  SYS_SPACE_RELATIVE_XL,
  SYS_SPACE_RELATIVE_XS,
  SYS_TYPOGRAPHY_ACTION_ORDER1_SHORT,
} from '@oneaudi/unified-web-common';

// Unified Legacy
const colorsLightLegacy = {
  colorCanvasDefault: process.env.IS_LEGACY ? GLOBAL_COLOR_CANVAS_DEFAULT_LIGHT : '',
  colorBaseWhite: process.env.IS_LEGACY ? GLOBAL_COLOR_CANVAS_DEFAULT_LIGHT : '',
  colorBaseBlack: process.env.IS_LEGACY ? GLOBAL_COLOR_CANVAS_DEFAULT_DARK : '',
  colorGreyLight: SYS_COLOR_FOREGROUND_NEUTRAL_200_DARK,
  colorBaseGrey5: process.env.IS_LEGACY ? SYS_COLOR_SURFACE_INTERACTIVE_SECONDARY_100_DARK : '',
  colorBaseGrey20: process.env.IS_LEGACY ? SYS_COLOR_SURFACE_INTERACTIVE_SECONDARY_300_DARK : '',
  colorBaseGrey60: SYS_COLOR_FOREGROUND_NEUTRAL_400_DARK,
  colorProgressiveRed: SYS_COLOR_SURFACE_HIGHLIGHT_PRIMARY_DARK,
  colorHover: process.env.IS_LEGACY ? GLOBAL_COLOR_ELEVATION_LEVEL3_DARK : '',
};

const colorsDarkLegacy = {
  colorCanvasDefault: SEM_COLOR_ACTION_PRIMARY_FOREGROUND_TEXT_DEFAULT_DARK,
  colorBaseWhite: SEM_COLOR_ACTION_PRIMARY_FOREGROUND_TEXT_DEFAULT_DARK,
  colorBaseBlack: SEM_COLOR_ACTION_PRIMARY_FOREGROUND_TEXT_DEFAULT_DARK,
  colorGreyLight: SYS_COLOR_FOREGROUND_NEUTRAL_200_LIGHT,
  colorBaseGrey5: process.env.IS_LEGACY ? SYS_COLOR_SURFACE_INTERACTIVE_SECONDARY_100_LIGHT : '',
  colorBaseGrey20: process.env.IS_LEGACY ? SYS_COLOR_SURFACE_INTERACTIVE_SECONDARY_300_LIGHT : '',
  colorBaseGrey60: SYS_COLOR_FOREGROUND_INTERACTIVE_NAVIGATION_200_DARK,
  colorProgressiveRed: SYS_COLOR_SURFACE_HIGHLIGHT_PRIMARY_LIGHT,
  colorHover: process.env.IS_LEGACY ? GLOBAL_COLOR_ELEVATION_LEVEL3_LIGHT : '',
};

const typographyLegacy = {
  fontFamily: process.env.IS_LEGACY
    ? SYS_TYPOGRAPHY_ACTION_ORDER1_SHORT.fontFamily
    : 'Audi Type Variable',
  fontSize: SYS_FONT_SIZES_RELATIVE_100,
  fontSizeM: SYS_FONT_SIZES_RELATIVE_112_5,
  fontSizeL: SYS_FONT_SIZES_RELATIVE_125,
  fontStretch: 'normal',
  lineHeight: SYS_FONT_SIZES_RELATIVE_125,
  bold: '700',
};

const spacing = {
  xs: SYS_SPACE_RELATIVE_XS,
  s: SYS_SPACE_RELATIVE_SM,
  m: SYS_SPACE_RELATIVE_MD,
  l: SYS_SPACE_RELATIVE_LG,
  xl: SYS_SPACE_RELATIVE_XL,
  xxl: SYS_SPACE_RELATIVE_2_XL,
  xxxl: SYS_SPACE_RELATIVE_4_XL,
};

// Alpha Web
const colorsLight = {
  colorCanvasDefault: SYS_COLOR_CANVAS_100_LIGHT,
  colorBaseWhite: SYS_COLOR_CANVAS_100_LIGHT,
  colorBaseBlack: SYS_COLOR_CANVAS_100_DARK,
  colorElevationLevel2Stops0: SYS_COLOR_ELEVATION_LEVEL2_FILL_STOPS_0_LIGHT,
  colorElevationLevel2Stops1: SYS_COLOR_ELEVATION_LEVEL2_FILL_STOPS_100_LIGHT,
  colorBaseGrey60: SEM_COLOR_NAVIGATION_FOREGROUND_TEXT_IDLE_LIGHT,
  colorSubNavLink: SYS_COLOR_FOREGROUND_INTERACTIVE_NAVIGATION_200_LIGHT,
  colorProgressiveRed: SEM_COLOR_FEEDBACK_SURFACE_INFORMATIVE_DARK,
  colorHover: SEM_COLOR_NAVIGATION_FOREGROUND_TEXT_ACTIVE_LIGHT,
  icon: SEM_COLOR_ACTION_TERTIARY_FOREGROUND_ICON_DEFAULT_LIGHT,
  divider: SEM_COLOR_UI_FOREGROUND_DIVIDER_DEFAULT_LIGHT,
};

const colorsDark = {
  colorCanvasDefault: SYS_COLOR_CANVAS_100_DARK,
  colorBaseWhite: SYS_COLOR_CANVAS_100_LIGHT,
  colorBaseBlack: SYS_COLOR_CANVAS_100_DARK,
  colorElevationLevel2Stops0: SYS_COLOR_ELEVATION_LEVEL2_FILL_STOPS_0_DARK,
  colorElevationLevel2Stops1: SYS_COLOR_ELEVATION_LEVEL2_FILL_STOPS_100_DARK,
  colorBaseGrey60: SEM_COLOR_NAVIGATION_FOREGROUND_TEXT_IDLE_DARK,
  colorSubNavLink: SYS_COLOR_FOREGROUND_INTERACTIVE_NAVIGATION_200_DARK,
  colorProgressiveRed: SEM_COLOR_FEEDBACK_SURFACE_INFORMATIVE_DARK,
  colorHover: SEM_COLOR_NAVIGATION_FOREGROUND_TEXT_ACTIVE_DARK,
  icon: SEM_COLOR_ACTION_TERTIARY_FOREGROUND_ICON_DEFAULT_DARK,
  divider: SEM_COLOR_UI_FOREGROUND_DIVIDER_DEFAULT_DARK,
};

const typography = {
  fontFamily: SEM_TYPOGRAPHY_NAVIGATION_SM_LABEL_NORMAL.fontFamily,
  fontSizeS: SYS_FONT_SIZES_RELATIVE_75,
  fontSizeM: SYS_FONT_SIZES_RELATIVE_85,
  fontSizeL: SYS_FONT_SIZES_RELATIVE_100,
  fontStretch: 'normal',
  lineHeight: SYS_FONT_SIZES_RELATIVE_125,
  bold: '400',
};

const breakpoints = {
  xs: parseInt(SYS_BREAKPOINT_XS, 10),
  s: parseInt(SYS_BREAKPOINT_SM, 10),
  m: parseInt(SYS_BREAKPOINT_MD, 10),
  l: parseInt(SYS_BREAKPOINT_LG, 10),
  xl: parseInt(SYS_BREAKPOINT_XL, 10),
  xxl: parseInt(SYS_BREAKPOINT_2_XL, 10),
  xxxl: parseInt(SYS_MAX_CONTENT_WIDTH, 10),
};

const sizing = {
  600: SYS_SIZE_FIXED_600,
  800: SYS_SIZE_FIXED_800,
  2400: SYS_SIZE_FIXED_2400,
};

const easing = {
  easingDefault: 'cubic-bezier(0.75, 0.02, 0.5, 1)',
};

const border = {
  radius: {
    none: SYS_BORDER_RADIUS_NONE,
    xs: SYS_BORDER_RADIUS_XS,
    s: SYS_BORDER_RADIUS_SM,
    m: SYS_BORDER_RADIUS_MD,
    max: SYS_BORDER_RADIUS_MAX,
  },
};

export const lightTheme: CustomTheme = {
  name: 'light',
  type: process.env.IS_LEGACY ? 'legacy' : 'alpha',
  colors: process.env.IS_LEGACY ? colorsLightLegacy : colorsLight,
  typography: process.env.IS_LEGACY ? typographyLegacy : typography,
  spacing: spacing,
  breakpoints: breakpoints,
  sizing,
  easing,
  border,
};

export const darkTheme: CustomTheme = {
  name: 'dark',
  type: process.env.IS_LEGACY ? 'legacy' : 'alpha',
  colors: process.env.IS_LEGACY ? colorsDarkLegacy : colorsDark,
  typography: process.env.IS_LEGACY ? typographyLegacy : typography,
  spacing: spacing,
  breakpoints: breakpoints,
  sizing,
  easing,
  border,
};
